import { useMemo, useState, type ReactNode } from 'react';
import {
  useCreateSignedIcs215Version,
  useCreateUnsignedIcs215Version,
  useIcs215Versions,
} from 'components/IAP/hooks/ics215FormVersionHooks';
import { selectUser, useAppSelector } from 'slices/commonSelectors';
import Ics204SignDialog from '../AssignmentList/Ics204SignDialog';
import Ics204VersionsDialog, {
  type WalaVersionRow,
} from '../AssignmentList/Ics204VersionsDialog';
import Ics204VersionStatusBar from '../AssignmentList/Ics204VersionStatusBar';
import { IcsPreparedBySection } from '../IcsPreparedByBox';
import { ICS215_BOX_LABELS } from './ics215BoxLabels';
import {
  getIcs215SignatureCompletion,
  ICS204_PREPARED_BY_SIGN_ROLE,
  parseWalaSignatures,
  type Ics215Signature,
} from './ics215SignatureSlots';

export type Ics215VersioningPanelProps = {
  ics215Id: string | null;
  periodNumber: number;
  tipId?: string | null;
  tipSignatures: Ics215Signature[];
  tipUpdatedAt?: string | null;
  tipUpdatedByUserName?: string | null;
  isCreatingSignedVersion: boolean;
  viewingVersionId: string | null;
  readOnly?: boolean;
  children?: ReactNode;
  onStartCreateSignedVersion: () => void;
  onCancelCreateSignedVersion: () => void;
  onSignedVersionCreated: () => void;
  onUnsignedVersionCreated: () => void;
  onViewVersion: (version: WalaVersionRow) => void;
  onViewLatest: () => void;
  /** Persist live worksheet before snapshotting a signed tip. */
  onBeforeSign?: () => Promise<void>;
};

const Ics215VersioningPanel = ({
  ics215Id,
  periodNumber,
  tipId,
  tipSignatures,
  tipUpdatedAt,
  tipUpdatedByUserName,
  isCreatingSignedVersion,
  viewingVersionId,
  readOnly = false,
  children,
  onStartCreateSignedVersion,
  onCancelCreateSignedVersion,
  onSignedVersionCreated,
  onUnsignedVersionCreated,
  onViewVersion,
  onViewLatest,
  onBeforeSign,
}: Ics215VersioningPanelProps) => {
  const user = useAppSelector(selectUser);
  const defaultSignName =
    user?.profile_settings?.userName?.trim() || user?.user_name?.trim() || '';
  const { data: versions = [] } = useIcs215Versions(ics215Id ?? '');
  const createSigned = useCreateSignedIcs215Version(periodNumber);
  const createUnsigned = useCreateUnsignedIcs215Version(periodNumber);

  const [versionsDialog, setVersionsDialog] = useState<'all' | 'signed' | null>(
    null
  );
  const [signOpen, setSignOpen] = useState(false);

  const viewingVersion = useMemo(
    () =>
      viewingVersionId
        ? versions.find((version) => version.id === viewingVersionId)
        : undefined,
    [viewingVersionId, versions]
  );

  const displaySignatures = useMemo(() => {
    if (!viewingVersion) return tipSignatures;
    return parseWalaSignatures(viewingVersion.signatures);
  }, [tipSignatures, viewingVersion]);

  const displayUpdatedAt = viewingVersion?.updatedAt ?? tipUpdatedAt ?? null;
  const displayUpdatedByUserName =
    viewingVersion?.updatedByUserName ?? tipUpdatedByUserName ?? null;

  const tipCompletion = getIcs215SignatureCompletion(tipSignatures);
  const signedCount = versions.filter(
    (version) =>
      getIcs215SignatureCompletion(parseWalaSignatures(version.signatures))
        .status === 'signed'
  ).length;

  const preparedBy = displaySignatures[0];

  const createItems = useMemo(() => {
    if (readOnly || viewingVersionId || !ics215Id) return [];
    const items: {
      key: string;
      label: string;
      onSelect: () => void;
      disabled?: boolean;
    }[] = [];

    items.push({
      key: 'signed',
      label: 'Create New Signed Version',
      onSelect: onStartCreateSignedVersion,
    });

    if (tipCompletion.status === 'signed') {
      items.push({
        key: 'unsigned',
        label: 'Create New Draft Version',
        onSelect: () => {
          createUnsigned.mutate(undefined, {
            onSuccess: () => onUnsignedVersionCreated(),
          });
        },
      });
    }

    return items;
  }, [
    createUnsigned,
    ics215Id,
    onStartCreateSignedVersion,
    onUnsignedVersionCreated,
    readOnly,
    tipCompletion.status,
    viewingVersionId,
  ]);

  if (!ics215Id) return null;

  const confirmSign = (name: string, positionTitle?: string) => {
    void (async () => {
      try {
        await onBeforeSign?.();
        createSigned.mutate(
          {
            name,
            role: ICS204_PREPARED_BY_SIGN_ROLE,
            positionTitle,
            signedAt: Date.now(),
          },
          {
            onSuccess: () => {
              setSignOpen(false);
              onSignedVersionCreated();
            },
          }
        );
      } catch {
        // Save errors toast from mutation; keep sign dialog open.
      }
    })();
  };

  return (
    <>
      <div className="tw:relative tw:min-w-0 tw:w-full">
        <Ics204VersionStatusBar
          signatures={displaySignatures}
          viewingPast={!!viewingVersionId}
          isCreatingSignedVersion={isCreatingSignedVersion}
          historyCount={versions.length}
          signedCount={signedCount}
          createItems={createItems}
          updatedAt={displayUpdatedAt}
          updatedByUserName={displayUpdatedByUserName}
          getCompletion={getIcs215SignatureCompletion}
          onOpenHistory={() => setVersionsDialog('all')}
          onOpenSigned={() => setVersionsDialog('signed')}
          onViewLatest={onViewLatest}
          onCancelCreateSigned={onCancelCreateSignedVersion}
          onSignThisVersion={
            isCreatingSignedVersion ? () => setSignOpen(true) : undefined
          }
        />
        {children}
        <div className="tw:px-3 tw:pt-2 tw:pb-1">
          <IcsPreparedBySection
            signature={preparedBy}
            boxLabel={ICS215_BOX_LABELS.preparedBy}
            testId="ics215-prepared-by-box"
            unsignedMessage="Not signed. Create a signed version above to sign this worksheet."
          />
        </div>
      </div>
      {versionsDialog ? (
        <Ics204VersionsDialog
          open
          kind={versionsDialog}
          versions={versions as WalaVersionRow[]}
          tipId={tipId}
          getCompletion={getIcs215SignatureCompletion}
          onClose={() => setVersionsDialog(null)}
          onView={(version) => {
            setVersionsDialog(null);
            onViewVersion(version);
          }}
        />
      ) : null}
      {signOpen ? (
        <Ics204SignDialog
          open
          role={ICS204_PREPARED_BY_SIGN_ROLE}
          title="Sign ICS-215 as Prepared By"
          description="Signing locks this version and freezes a snapshot of the worksheet for export."
          defaultName={defaultSignName}
          collectPositionTitle
          isPending={createSigned.isPending}
          onClose={() => setSignOpen(false)}
          onConfirm={confirmSign}
        />
      ) : null}
    </>
  );
};

export default Ics215VersioningPanel;
